import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import Seo from "../../components/Seo";
import { apiErrorMessage, userAuth, userService } from "../../services/userApi";
import "../../components/lawyer/lawyer.css";
import "../../components/user/user.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FieldError = ({ msg }) => (msg ? <small className="lwp-signup-error">{msg}</small> : null);

const UserSignup = () => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState({ value: "971", dialCode: "971" });
  const [accepted, setAccepted] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [guestLoading, setGuestLoading] = useState(false);

  const clearError = (key) => setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));

  const validate = () => {
    const e = {};
    const nationalNumber = phone.value.slice(phone.dialCode.length);
    if (!name.trim()) e.name = "Full name is required.";
    if (!email.trim()) e.email = "Email is required.";
    else if (!EMAIL_RE.test(email.trim())) e.email = "Please enter a valid email address.";
    if (!nationalNumber) e.phone = "Phone number is required.";
    else if (nationalNumber.length < 6) e.phone = "Please enter a valid phone number.";
    if (!accepted) e.terms = "Please accept the Privacy policy and Terms & conditions.";
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.values(e).some(Boolean)) return;

    const value = email.trim();
    setSaving(true);
    try {
      const res = await userService.register({ name: name.trim(), email: value, phone: `+${phone.value}` });
      if (res?.data?.status) {
        toast.success(res.data.message || "OTP sent to your email");
        userAuth.setPendingEmail(value);
        navigate("/user/verify-otp", { state: { email: value, from: "signup" } });
      } else {
        toast.error(res?.data?.message || "Unable to create account.");
      }
    } catch (err) {
      // Field errors (e.g. email/phone already taken) go under their inputs.
      const fieldErrors = err?.response?.data?.errors;
      if (fieldErrors) {
        setErrors(Object.fromEntries(Object.entries(fieldErrors).map(([k, v]) => [k, v?.[0]])));
      }
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const continueAsGuest = async () => {
    if (guestLoading) return;
    setGuestLoading(true);
    try {
      const res = await userService.guestLogin();
      const data = res?.data?.data;
      if (res?.data?.status && data?.auth_token) {
        userAuth.setSession(data.auth_token, data.user);
        navigate("/user/dashboard", { replace: true });
      } else {
        toast.error(res?.data?.message || "Unable to continue as guest.");
      }
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setGuestLoading(false);
    }
  };

  return (
    <div className="lwp lwp-auth usp">
      <Seo title="Create Account | Legal Platform" noindex />
      <div className="lwp-auth-card usp-signup-card">
        <h1 className="lwp-auth-title">Create Account</h1>

        <form onSubmit={handleSubmit} noValidate>
          <div>
            <input
              className={`lwp-input${errors.name ? " usp-invalid" : ""}`}
              placeholder="Full Name*"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                clearError("name");
              }}
              autoComplete="name"
              autoFocus
            />
            <FieldError msg={errors.name} />
          </div>

          <div>
            <input
              type="email"
              className={`lwp-input${errors.email ? " usp-invalid" : ""}`}
              placeholder="Email*"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                clearError("email");
              }}
              autoComplete="email"
            />
            <FieldError msg={errors.email} />
          </div>

          <div>
            <PhoneInput
              country="ae"
              value={phone.value}
              onChange={(value, data) => {
                setPhone({ value, dialCode: data?.dialCode || "" });
                clearError("phone");
              }}
              countryCodeEditable={false}
              enableSearch
              searchPlaceholder="Search country..."
              placeholder="Phone Number*"
              containerClass={`lwp-phone${errors.phone ? " is-invalid" : ""}`}
              inputProps={{ name: "phone", autoComplete: "tel" }}
            />
            <FieldError msg={errors.phone} />
          </div>

          <button type="submit" className="lwp-btn" disabled={saving}>
            {saving ? <span className="spinner-border spinner-border-sm" /> : "Sign Up"}
          </button>
          <button type="button" className="usp-btn-ghost usp-btn-ghost-strong" onClick={continueAsGuest} disabled={guestLoading}>
            {guestLoading ? <span className="spinner-border spinner-border-sm" /> : "Continue with Guest"}
          </button>

          <div>
            <label className="lwp-check">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => {
                  setAccepted(e.target.checked);
                  clearError("terms");
                }}
              />
              <span>
                Accept all <Link to="/privacy" target="_blank">Privacy policy</Link> and{" "}
                <Link to="/terms" target="_blank">Terms &amp; conditions</Link>.
              </span>
            </label>
            {errors.terms && <small className="lwp-signup-error text-center m-0">{errors.terms}</small>}
          </div>
        </form>

        <p className="lwp-auth-footer usp-auth-footer">
          Already have an account? <Link to="/user/login">Login</Link>
        </p>
      </div>
    </div>
  );
};

export default UserSignup;
